import { openDB, DBSchema, IDBPDatabase } from 'idb'

export type RecordingStatus = 'pending' | 'uploading' | 'done' | 'error'

export interface OfflineRecording {
  id: string
  title: string
  blob: Blob
  mimeType: string
  durationSec: number
  createdAt: number
  status: RecordingStatus
  documentId?: string
  error?: string
}

interface RecordingsDB extends DBSchema {
  recordings: {
    key: string
    value: OfflineRecording
    indexes: { 'by-status': string; 'by-createdAt': number }
  }
}

let dbPromise: Promise<IDBPDatabase<RecordingsDB>> | null = null

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB<RecordingsDB>('bluepaper-offline-recordings', 1, {
      upgrade(db) {
        const store = db.createObjectStore('recordings', { keyPath: 'id' })
        store.createIndex('by-status', 'status')
        store.createIndex('by-createdAt', 'createdAt')
      }
    })
  }
  return dbPromise
}

export async function saveRecording(rec: OfflineRecording): Promise<void> {
  const db = await getDB()
  await db.put('recordings', rec)
}

export async function getRecording(id: string): Promise<OfflineRecording | undefined> {
  const db = await getDB()
  return db.get('recordings', id)
}

export async function listRecordings(): Promise<OfflineRecording[]> {
  const db = await getDB()
  const all = await db.getAll('recordings')
  return all.sort((a, b) => b.createdAt - a.createdAt)
}

export async function listPending(): Promise<OfflineRecording[]> {
  const db = await getDB()
  const pending = await db.getAllFromIndex('recordings', 'by-status', 'pending')
  return pending.sort((a, b) => a.createdAt - b.createdAt)
}

export async function updateRecording(
  id: string,
  patch: Partial<Omit<OfflineRecording, 'id'>>
): Promise<OfflineRecording | undefined> {
  const db = await getDB()
  const rec = await db.get('recordings', id)
  if (!rec) return undefined
  const next = { ...rec, ...patch }
  await db.put('recordings', next)
  return next
}

export async function deleteRecording(id: string): Promise<void> {
  const db = await getDB()
  await db.delete('recordings', id)
}
